import React, { Fragment, useEffect, useRef, useState } from "react";
import { Combobox } from "@headlessui/react";
import ChevronDown from "../../Atomos/Icons/ChevronDown";
import WSGCheck from "../../Atomos/Icons/WSGCheck";
import NewTooltip from "../Tooltip/NewTooltip";
import { useStaffMode } from "../../../hooks/StaffModeProvider";
import { foldSearchText, includesFoldedSearch } from "../../../utils/foldSearchText";

type OptionWithParent = Option & {
  parent?: string;
};

type Option = {
  id?: string;
  name: string;
  selected?: any;
  disable?: boolean;
  firms?: any | null;
  offices?: any | null;
};

type dDMultipleProps = {
  disabled?: boolean;
  title: string;
  objects: Option[];
  selectedObjst: Option[];
  seter: React.Dispatch<React.SetStateAction<Option[]>>;
  error?: boolean;
  required?: boolean;
  errorMessage?: string;
  placeholder?: string;
  tooltip?: string;
  dynamic?: boolean;
  hideCheckboxes?: boolean;
  disableWrite?: boolean;
  titleClass?: string;
  ddclass?: string;
  ddOptionsClass?: string;
  ddOptionsItemClass?: string;
  rightButton?: React.ReactNode;
  disableSort?: boolean;
  internalSearch?: boolean;
  /** Show Select all / Clear all under the search field (use with internalSearch). */
  bulkActions?: boolean;
  onBulkSelectAll?: () => void;
  onBulkClearAll?: () => void;
};

const areOptionsEqual = (option1: Option, option2: Option): boolean => {
  if (option1.id && option2.id && option1.id === option2.id) {
    return true;
  }
  if (option1.name && option2.name && option1.name === option2.name) {
    return true;
  }
  return false;
};

/** Sync Headless UI Combobox `open` into React state without setState during render. */
function ComboboxOpenSync({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  useEffect(() => {
    onOpenChange(open);
  }, [open, onOpenChange]);
  return null;
}

function DDMultiple(props: dDMultipleProps) {
  const sourceObjects = Array.isArray(props.objects) ? props.objects : [];
  const selectedList = Array.isArray(props.selectedObjst) ? props.selectedObjst : [];
  const objects: Option[] = React.useMemo(() => {
    if (props.disableSort) {
      return [...sourceObjects];
    }
    return [...sourceObjects].sort((a, b) => a.name?.localeCompare(b.name));
  }, [sourceObjects, props.disableSort]);
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const { isStaffMode } = useStaffMode();

  const filteredAndSortedObjects =
    query === ""
      ? objects
      : objects
          .filter((object) =>
            includesFoldedSearch(String(object.name ?? ""), query),
          )
          .sort((a, b) => {
            const nameA = foldSearchText(a.name ?? "override");
            const nameB = foldSearchText(b.name ?? "override");
            if (nameA < nameB) {
              return -1;
            }
            if (nameA > nameB) {
              return 1;
            }
            return 0;
          });

  useEffect(() => {
    if (selectedList.length === 0) {
      setQuery("");
    }
  }, [selectedList.length]);

  useEffect(() => {
    if (props.internalSearch && !isOpen) {
      setQuery("");
    }
  }, [isOpen, props.internalSearch]);

  const inputRef = useRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [inputWidth, setInputWidth] = useState<number>(0);

  useEffect(() => {
    const updateInputWidth = () => {
      if (inputRef.current) {
        setInputWidth(inputRef.current.offsetWidth);
      }
    };

    updateInputWidth();
    window.addEventListener("resize", updateInputWidth);
    return () => {
      window.removeEventListener("resize", updateInputWidth);
    };
  }, []);

  useEffect(() => {
    if (inputWidth < 140) {
      inputRef.current?.setAttribute("placeholder", "");
    } else {
      inputRef.current?.setAttribute(
        "placeholder",
        props.placeholder ? props.placeholder : "Select Multiple"
      );
    }
  }, [inputWidth, props.placeholder]);

  const renderCheckbox = (selected: boolean) => {
    if (props.hideCheckboxes) {
      return null;
    }
    return (
      <div
        className={`stroke-gray-font stroke-2 ${
          !selected
            ? "fill-neutral"
            : isStaffMode && selected
            ? "fill-secondary"
            : "fill-primary"
        } h-5 w-5 min-w-[20px] min-h-[20px]`}
      >
        <WSGCheck check={selected} />
      </div>
    );
  };

  const shouldOpenFromFieldClick =
    !props.disabled && (props.disableWrite || props.internalSearch);

  const handleFieldMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!shouldOpenFromFieldClick) {
      return;
    }
    if ((event.target as HTMLElement).closest("[data-ddmultiple-trigger]")) {
      return;
    }

    event.preventDefault();
    buttonRef.current?.click();
    inputRef.current?.focus();
  };

  return (
    <>
      <div
        className={`${props.ddclass} flex flex-col gap-2 md:gap-[.625rem] w-full`}
      >
        {props.title && (
          <div className="flex gap-2 w-full flex-1">
            <label
              htmlFor={props.title}
              className={`${props.titleClass} text-base md:text-lg font-medium text-gray-font`}
            >
              {props.title}{" "}
              {props.required && <span className="text-primary"> *</span>}
            </label>
            {props.tooltip && <NewTooltip text={props.tooltip} />}
          </div>
        )}

        <div className="flex">
          <div className="flex-1">
            <Combobox<Option>
              value={selectedList}
              multiple
              disabled={props.disabled}
            >
              {({ open }) => (
                <div className="relative">
                    <ComboboxOpenSync open={open} onOpenChange={setIsOpen} />
                    <div
                      className={`relative items-start self-stretch ${
                        props.error && "errorInput"
                      }`}
                      onMouseDown={handleFieldMouseDown}
                    >
                      <Combobox.Input
                        ref={inputRef}
                        className={`${
                          props.ddclass
                        } bg-transparent text-base md:text-lg leading-5 font-normal not-italic border-none w-full ${
                          props.disabled
                            ? "text-gray-200 border border-solid border-gray-700 border-1"
                            : props.error
                            ? "text-primary focus:outline-none"
                            : "text-input-gray focus:outline-none"
                        } ${
                          props.disableWrite &&
                          "pointer-events-none cursor-default"
                        }`}
                        displayValue={() =>
                          selectedList
                            .map((object: Option) =>
                              object?.name ? object.name : "Override"
                            )
                            .join(", ")
                        }
                        onFocus={() => !props.internalSearch && setQuery("")}
                        onChange={(event) =>
                          !props.internalSearch && setQuery(event.target.value)
                        }
                        onReset={() => props.seter([] as Option[])}
                        readOnly={props.disableWrite || props.internalSearch}
                      />
                      <Combobox.Button
                        ref={buttonRef}
                        data-ddmultiple-trigger
                        className="absolute inset-y-0 right-0 flex items-center pr-2"
                      >
                        {!open ? (
                          <div
                            id="chevron-up"
                            className={`w-6 h-6 ${
                              props.disabled ? "text-gray-200" : "text-gray-font"
                            }`}
                          >
                            <ChevronDown />
                          </div>
                        ) : (
                          <div id="chevron-down" className="w-6 h-6 rotate-180">
                            <ChevronDown />
                          </div>
                        )}
                      </Combobox.Button>
                    </div>
                    <Combobox.Options
                      className={`absolute flex flex-col w-full gap-x-3 opacity-1 visible scale-[100%] top-30 transform transition-transform 
               ease-out duration-200 transition-delay-200 z-[100] bg-white shadow-lg ring-1 ring-black 
               ring-opacity-5 focus:outline-none ${props.ddOptionsClass}`}
                    >
                      {props.internalSearch && (
                        <div className="sticky top-0 bg-white p-2 z-10 border-b border-gray-100">
                          <div className="relative">
                            <input
                              type="text"
                              className="w-full"
                              placeholder="Search..."
                              value={query}
                              onChange={(event) => setQuery(event.target.value)}
                              onClick={(event) => event.stopPropagation()}
                            />
                            <div className="absolute inset-y-0 right-2 flex items-center pointer-events-none">
                              <svg
                                className="w-5 h-5 text-[#484F56]"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                />
                              </svg>
                            </div>
                          </div>
                          {props.bulkActions && objects.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              <button
                                type="button"
                                className="rounded-sm border border-gray-200 bg-gray-25 px-2 py-1 text-xs font-medium text-input-gray hover:bg-gray-100 md:text-sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  props.onBulkSelectAll?.();
                                }}
                              >
                                Select all
                              </button>
                              <button
                                type="button"
                                className="rounded-sm border border-gray-200 bg-gray-25 px-2 py-1 text-xs font-medium text-input-gray hover:bg-gray-100 md:text-sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  props.onBulkClearAll?.();
                                }}
                              >
                                Clear all
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                      <div
                        className={`${
                          props.internalSearch ? "max-h-[320px]" : "max-h-[400px]"
                        } no-scrollbar overflow-scroll flex flex-col`}
                      >
                        {filteredAndSortedObjects?.map((object: Option) => {
                          const isSelected = selectedList.some((selectedObj) =>
                            areOptionsEqual(selectedObj, object)
                          );
                          return (
                            <Combobox.Option
                              key={object?.id || object?.name}
                              value={object}
                              as={Fragment}
                              disabled={props.disabled}
                            >
                              {({ active }) => (
                                <li
                                  className={`flex gap-[0.625rem] !py-3 pl-4 pr-3 !lg:py-3 lg:pl-5 lg:pr-4 align-middle text-base md: leading-[19px] text-gray-font bg-white hover:bg-gray-50 cursor-pointer ${props.ddOptionsItemClass}`}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (isSelected) {
                                      // Deselect: remove from array
                                      props.seter(
                                        selectedList.filter(
                                          (obj) => !areOptionsEqual(obj, object)
                                        )
                                      );
                                    } else {
                                      // Select: add to array (with deduplication)
                                      const newSelection = [
                                        ...selectedList,
                                        object,
                                      ];
                                      const uniqueOptions = newSelection.filter(
                                        (option, index, self) =>
                                          index ===
                                          self.findIndex((o) =>
                                            areOptionsEqual(o, option)
                                          )
                                      );
                                      props.seter(uniqueOptions);
                                    }
                                  }}
                                >
                                  {renderCheckbox(isSelected)}
                                  <div className="first-letter:uppercase">
                                    {object.name}
                                  </div>
                                </li>
                              )}
                            </Combobox.Option>
                          );
                        })}
                        {!filteredAndSortedObjects?.length && (
                          <div className="px-4 py-2 text-base md:text-lg text-gray-500 text-center">
                            No results found
                          </div>
                        )}
                      </div>
                    </Combobox.Options>
                  </div>
              )}
            </Combobox>
          </div>
          {props.rightButton && (
            <div className="flex-shrink-0">{props.rightButton}</div>
          )}
        </div>

        {props.errorMessage && (
          <span
            className={`flex flex-wrap items-center text-base font-medium text-primary min-h-[1.5rem] ${
              !props.error && "invisible"
            }`}
          >
            {props.errorMessage}
          </span>
        )}
      </div>
    </>
  );
}

export default DDMultiple;
export type { Option, OptionWithParent };
